import {
  ACTION_LABELS, BIND_ACTIONS, bindButton, bindKey, buttonLabel, clearKey, defaultBindings, keyLabel, KEY_SLOTS, RESERVED_KEYS,
  type BindAction, type Bindings,
} from '../core/bindings';
import { clear, h } from './dom';
import { lastPadFamily } from './inputPrompts';

export interface ControlsActions {
  get(): Bindings;
  set(next: Bindings): void;
  /** Pause gamepad menu navigation while waiting for a button, so A doesn't also click. */
  suspendNav(on: boolean): void;
}

const CAPTURE_TIMEOUT_MS = 8000;

/** Rebinding table: two keyboard slots and one controller button per action. */
export function controlsPanel(a: ControlsActions): HTMLElement {
  const notice = h('div', { class: 'controls-notice sub', 'aria-live': 'polite' });
  const table = h('div', { class: 'controls-table', role: 'table', 'aria-label': 'Control bindings' });
  let cancelCapture: (() => void) | null = null;

  const say = (text: string) => { notice.textContent = text; };
  const apply = (next: Bindings, movedFrom: BindAction | null, action: BindAction, what: string) => {
    a.set(next);
    say(movedFrom ? `${what} moved from ${ACTION_LABELS[movedFrom]} to ${ACTION_LABELS[action]}.` : `${ACTION_LABELS[action]}: ${what}.`);
    render();
  };

  const captureKey = (action: BindAction, slot: number, button: HTMLElement) => {
    cancelCapture?.();
    button.textContent = 'Press a key…';
    button.classList.add('capturing');
    say('Press a key · Esc cancels · Backspace clears this slot');
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.repeat) return;
      done();
      if (e.code === 'Escape') { say('Cancelled.'); render(); return; }
      if (e.code === 'Backspace') { a.set(clearKey(a.get(), action, slot)); say(`${ACTION_LABELS[action]}: slot cleared.`); render(); return; }
      if (RESERVED_KEYS.has(e.code)) { say(`${keyLabel(e.code)} is reserved and can't be bound.`); render(); return; }
      const result = bindKey(a.get(), action, slot, e.code);
      apply(result.bindings, result.movedFrom, action, keyLabel(e.code));
    };
    const timer = window.setTimeout(() => { done(); say('Timed out.'); render(); }, CAPTURE_TIMEOUT_MS);
    const done = () => {
      window.removeEventListener('keydown', onKey, true);
      window.clearTimeout(timer);
      cancelCapture = null;
    };
    window.addEventListener('keydown', onKey, true);
    cancelCapture = done;
  };

  const captureButton = (action: BindAction, button: HTMLElement) => {
    cancelCapture?.();
    button.textContent = 'Press a button…';
    button.classList.add('capturing');
    say('Press a controller button · Esc cancels');
    a.suspendNav(true);
    let released = false;
    let frame = 0;
    const firstPad = () => Array.from(navigator.getGamepads?.() ?? []).find((p): p is Gamepad => !!p?.connected) ?? null;
    const poll = () => {
      const pad = firstPad();
      if (pad) {
        const pressed = pad.buttons.findIndex((b) => b.pressed);
        // Wait for the press that opened the capture (A) to be released first.
        if (!released) released = pressed < 0;
        else if (pressed >= 0) {
          done();
          const result = bindButton(a.get(), action, pressed);
          // Keep menu nav suspended until that press is released too.
          waitRelease(() => a.suspendNav(false));
          apply(result.bindings, result.movedFrom, action, buttonLabel(pressed, lastPadFamily()));
          return;
        }
      }
      frame = requestAnimationFrame(poll);
    };
    const waitRelease = (then: () => void) => {
      const tick = () => (firstPad()?.buttons.some((b) => b.pressed) ? requestAnimationFrame(tick) : then());
      tick();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      done();
      a.suspendNav(false);
      say('Cancelled.');
      render();
    };
    const timer = window.setTimeout(() => { done(); a.suspendNav(false); say('Timed out.'); render(); }, CAPTURE_TIMEOUT_MS);
    const done = () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey, true);
      window.clearTimeout(timer);
      cancelCapture = null;
    };
    window.addEventListener('keydown', onKey, true);
    cancelCapture = () => { done(); a.suspendNav(false); };
    frame = requestAnimationFrame(poll);
  };

  const render = () => {
    clear(table);
    const b = a.get();
    const family = lastPadFamily();
    table.append(h('div', { class: 'controls-row controls-head', role: 'row' },
      h('span', { role: 'columnheader' }, 'Action'),
      h('span', { role: 'columnheader' }, 'Key'),
      h('span', { role: 'columnheader' }, 'Alt key'),
      h('span', { role: 'columnheader' }, 'Controller'),
    ));
    for (const action of BIND_ACTIONS) {
      const keys = Array.from({ length: KEY_SLOTS }, (_, slot) => {
        const code = b.keyboard[action][slot];
        const button = h('button', { class: 'small controls-slot', 'aria-label': `${ACTION_LABELS[action]} key ${slot + 1}: ${code ? keyLabel(code) : 'unbound'}` }, code ? keyLabel(code) : '—');
        button.addEventListener('click', () => captureKey(action, slot, button));
        return button;
      });
      const pad = b.gamepad[action][0];
      const padButton = h('button', { class: 'small controls-slot', 'aria-label': `${ACTION_LABELS[action]} controller: ${pad !== undefined ? buttonLabel(pad, family) : 'unbound'}` }, pad !== undefined ? buttonLabel(pad, family) : '—');
      padButton.addEventListener('click', () => captureButton(action, padButton));
      table.append(h('div', { class: 'controls-row', role: 'row' }, h('span', { role: 'rowheader' }, ACTION_LABELS[action]), ...keys, padButton));
    }
  };

  const root = h('div', { class: 'controls-panel col' },
    table,
    notice,
    h('div', { class: 'row controls-actions' },
      h('button', { class: 'small', onClick: () => { cancelCapture?.(); a.set(defaultBindings()); say('Controls reset to defaults.'); render(); } }, 'Reset to defaults'),
    ),
    h('div', { class: 'sub controls-footnote' }, 'Esc always pauses and goes back. On a controller, A/B (✕/○) always confirm and go back in menus, and the left stick always moves.'),
  );
  render();
  return root;
}
